// Icons
import icons from "enums/icons";
import CustomIcon from "components/CustomIcon";
import { CompoundPermission } from "context/Ability";
import { urlPaths } from "enums/urlPaths";
import { ChartRouteNames } from "enums/chartEnums";
import {
    updateLDDashboardItem,
    updateLDInsightsItem,
    updateLDPathToImprovementItem,
    updateLDSearchAiItem,
    updatePaceItems,
} from "./mainMenu.functions";

export type MenuItem = {
    id: string;
    label: string;
    startIcon: React.ReactNode | null;
    endIcon: React.ReactNode | null;
    disabled: boolean;
    isSelected: boolean;
    permissions: CompoundPermission[];
    verticalSpace?: number;
    parentId?: string;
    slide?: number;
    goToSlide?: number;
    indent?: boolean;
    shouldExpand?: boolean;
    isExpanded?: boolean;
    base?: string;
    path?: string;
    url?: string;
    extraPaths?: string[];
    updatePathAndItems?: any;
    children?: MenuItem[][];
};

export const menuConfig: MenuItem[] = [
    {
        id: "hub-management",
        slide: 0,
        label: "Hub Management",
        parentId: undefined,
        startIcon: undefined,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 2,
        url: urlPaths.HubManagement,
        base: urlPaths.HubManagement,
        path: "",
        permissions: [{ list: ["SYS_ADMIN_VIEW_CLIENT_HUB"], operator: "or" }],
    },
    {
        id: "user-management",
        slide: 0,
        label: "User Management",
        parentId: undefined,
        startIcon: undefined,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 2,
        url: urlPaths.UserManagement,
        base: urlPaths.UserManagement,
        path: "",
        permissions: [{ list: ["SYS_ADMIN_VIEW_USER_MANAGEMENT"], operator: "or" }],
    },
    {
        id: "my-dashboard",
        slide: 0,
        label: "My Dashboard",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.mainMenuDashboard} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/pace",
        path: "/my-dashboard",
        url: urlPaths.PaceLabsDashboard,
        extraPaths: ["/pace/assessment", "pace/my-pace", "/pace/report", "/pace/my-experience", "/pace/my-insights"],
        updatePathAndItems: updatePaceItems,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PROFILE", "SAAS_PROFILE_VIEW_DASHBOARD"], operator: "and" },
        ],
    },
    {
        id: "pace-assessment",
        slide: 0,
        label: "PACE Assessment",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.paceIconColoredAlt} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: true,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/pace",
        path: "/report",
        url: "/pace/report/user",
        updatePathAndItems: updatePaceItems,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT"], operator: "and" },
            { list: ["PACE_REPORT_RUN_OWN", "PACE_REPORT_RUN_GRP"], operator: "or" },
            { list: ["SAAS_PACE", "SAAS_PACE_ASSESSMENT"], operator: "and" },
        ],
    },
    {
        id: "my-pace",
        slide: 0,
        label: "My PACE",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.sparkles} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: true,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/pace",
        path: "/my-pace",
        url: urlPaths.MyPace,
        updatePathAndItems: updatePaceItems,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT"], operator: "and" },
            { list: ["PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PACE_REPORT"], operator: "and" },
        ],
    },
    {
        id: "my-experience",
        slide: 0,
        label: "My Experience",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.sparkles} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: true,
        isSelected: false,
        disabled: false,
        verticalSpace: 2,
        base: "/pace",
        path: "/my-experience",
        url: urlPaths.MyExperience,
        extraPaths: ["/pace/my-insights"],
        updatePathAndItems: updatePaceItems,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PROFILE", "SAAS_PROFILE_VIEW_INSIGHTS"], operator: "and" },
        ],
    },
    {
        id: "pace-changer-0",
        slide: 0,
        goToSlide: 2,
        label: "PACE",
        parentId: undefined,
        startIcon: undefined,
        endIcon: <CustomIcon icon={icons.arrowRight} sx={{ fontSize: 10 }} />,
        disabled: false,
        isSelected: false,
        verticalSpace: 2,
        permissions: [
            { list: ["SYS_ADMIN_VIEW_PACE_PORTAL", "SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON"], operator: "or" },
        ],
    },
    {
        id: "ld-changer-0",
        slide: 0,
        goToSlide: 1,
        label: "Leadership Dynamics",
        parentId: undefined,
        startIcon: undefined,
        endIcon: <CustomIcon icon={icons.arrowRight} sx={{ fontSize: 10 }} />,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_SEARCH_COMPANY"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_VIEW_GRP", "SAAS_LD_PROJECT_SEARCH_GRP"],
                operator: "or",
            },
        ],
    },
    // Leadership Dynamics
    {
        id: "ld-changer-1",
        slide: 1,
        goToSlide: 0,
        label: "Leadership Dynamics",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.arrowLeft} sx={{ fontSize: 10 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 2,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_SEARCH_COMPANY"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_VIEW_GRP", "SAAS_LD_PROJECT_SEARCH_GRP"],
                operator: "or",
            },
        ],
    },
    {
        id: "organisations",
        slide: 1,
        label: "Organisations",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.mainMenuLD} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/leadership-dynamics",
        path: "/my-searches",
        url: urlPaths.MySearches,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_SEARCH_SAVED_OWN", "LD_SEARCH_SAVED_GRP"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_VIEW_GRP", "SAAS_LD_PROJECT_SEARCH_GRP"],
                operator: "or",
            },
        ],
    },
    {
        id: "dashboard",
        slide: 1,
        label: "Dashboard",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.mainMenuDashboard} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        disabled: true,
        isSelected: false,
        verticalSpace: 1,
        base: "/leadership-dynamics",
        path: "/dashboard",
        url: urlPaths.ProjectDashboard,
        extraPaths: [
            "/leadership-dynamics/domain",
            "/leadership-dynamics/team",
            "/leadership-dynamics/vcp",
            "/leadership-dynamics/insights",
        ],
        updatePathAndItems: updateLDDashboardItem,
        permissions: [
            { list: ["LD_SEARCH_COMPANY", "LD_PROJECT_VIEW"], operator: "and" },
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_PROJECT_EDIT", "SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
    },
    {
        id: "ld-insights",
        slide: 1,
        label: "Insights",
        indent: true,
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.sparkles} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        disabled: true,
        isSelected: false,
        shouldExpand: true,
        isExpanded: false,
        verticalSpace: 1,
        extraPaths: [
            "/leadership-dynamics/domain",
            "/leadership-dynamics/team",
            "/leadership-dynamics/vcp",
            "/leadership-dynamics/insights/{companyId}/{childId}",
        ],
        updatePathAndItems: updateLDInsightsItem,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_PROJECT_EDIT"],
                operator: "or",
            },
            { list: ["SAAS_LD_PROJECT"], operator: "or" },
        ],
        children: [
            [
                {
                    id: "set-domain",
                    label: "Set Domain",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/domain",
                    url: "/leadership-dynamics/domain",
                    permissions: [
                        {
                            list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_PROJECT_EDIT"],
                            operator: "or",
                        },
                        { list: ["SAAS_LD_PROJECT"], operator: "or" },
                    ],
                },
                {
                    id: "team",
                    label: "Set Team",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/team",
                    url: "/leadership-dynamics/team",
                    permissions: [
                        {
                            list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TEAM", "LD_TALENT", "LD_PROJECT_EDIT"],
                            operator: "or",
                        },
                        {
                            list: ["SAAS_LD_PROJECT"],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: "vcp",
                    label: "Set VCP",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/vcp",
                    url: "/leadership-dynamics/vcp",
                    permissions: [
                        {
                            list: ["LD_TEAM", "LD_PROJECT_EDIT"],
                            operator: "or",
                        },
                        {
                            list: ["SAAS_LD_PROJECT"],
                            operator: "or",
                        },
                    ],
                },
            ],
            [
                {
                    id: ChartRouteNames.Balance,
                    label: "Balance",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.Positioning,
                    label: "Individual Positioning",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.Behavioural,
                    label: "Behaviours",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.BehaviouralBreakdown,
                    label: "Behavioural Breakdown",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.Potential,
                    label: "Potential",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.Comparison,
                    label: "Comparison",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.Situational,
                    label: "Situational",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.Domain,
                    label: "Domain",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.Functional,
                    label: "Functional",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: ChartRouteNames.Overall,
                    label: "Overall LSPM",
                    parentId: "ld-insights",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        {
                            list: [
                                "LD_FREE",
                                "LD_LIMITED",
                                "LD_BASIC",
                                "LD_PROJECT_EDIT",
                                "LD_TALENT",
                                "SAAS_LD_PROJECT",
                            ],
                            operator: "or",
                        },
                    ],
                },
            ],
        ],
    },
    {
        id: "path-to-improvement",
        slide: 1,
        label: "Path to Improvement",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.pathToImprovement} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: true,
        shouldExpand: true,
        isExpanded: false,
        disabled: true,
        isSelected: false,
        verticalSpace: 1,
        extraPaths: [
            "/leadership-dynamics/insights/{companyId}/bridge",
            "/leadership-dynamics/brief",
            "/leadership-dynamics/brief/review",
        ],
        updatePathAndItems: updateLDPathToImprovementItem,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
        children: [
            [
                {
                    id: ChartRouteNames.Bridge,
                    label: "Bridge",
                    parentId: "path-to-improvement",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/insights",
                    url: "/leadership-dynamics/insights",
                    permissions: [
                        { list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_BRIEF_CREATE"], operator: "or" },
                        { list: ["LD_TALENT"], operator: "or" },
                    ],
                },
                {
                    id: "create-brief",
                    label: "Create Brief",
                    parentId: "path-to-improvement",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/brief",
                    url: "/leadership-dynamics/brief",
                    permissions: [
                        { list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_BRIEF_CREATE"], operator: "or" },
                        { list: ["LD_TALENT"], operator: "or" },
                    ],
                },
                {
                    id: "review-brief",
                    label: "Review Brief",
                    parentId: "path-to-improvement",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dynamics",
                    path: "/brief/review",
                    url: "/leadership-dynamics/brief/review",
                    permissions: [
                        { list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_BRIEF_CREATE"], operator: "or" },
                        { list: ["LD_TALENT"], operator: "or" },
                    ],
                },
            ],
        ],
    },
    {
        id: "search-ai",
        slide: 1,
        label: "Search AI",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.flashLight} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: true,
        isSelected: false,
        shouldExpand: true,
        isExpanded: false,
        disabled: true,
        verticalSpace: 1,
        extraPaths: ["/talent/companies", "/talent/people", "/talent/refinement"],
        updatePathAndItems: updateLDSearchAiItem,
        permissions: [
            { list: ["LD_TALENT"], operator: "or" },
            { list: ["SAAS_LD_PROJECT_SEARCH_AI_CREATE"], operator: "and" },
        ],
        children: [
            [
                {
                    id: "company-search",
                    label: "Company Search",
                    parentId: "search-ai",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    verticalSpace: 1,
                    base: "/talent",
                    path: "/companies",
                    url: "/talent/companies",
                    permissions: [
                        { list: ["LD_TALENT"], operator: "or" },
                        { list: ["SAAS_LD_PROJECT_SEARCH_AI_COMPANY"], operator: "or" },
                    ],
                },
                {
                    id: "people-search",
                    label: "People Search",
                    parentId: "search-ai",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    verticalSpace: 1,
                    base: "/talent",
                    path: "/people",
                    url: "/talent/people",
                    permissions: [
                        { list: ["LD_TALENT"], operator: "or" },
                        { list: ["SAAS_LD_PROJECT_SEARCH_AI_PEOPLE"], operator: "or" },
                    ],
                },
                {
                    id: "refinement",
                    label: "Refinement",
                    parentId: "search-ai",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    verticalSpace: 1,
                    base: "/talent",
                    path: "/refinement",
                    url: "/talent/refinement",
                    permissions: [
                        { list: ["LD_TALENT"], operator: "or" },
                        { list: ["SAAS_LD_PROJECT_SEARCH_AI_REFINE"], operator: "or" },
                    ],
                },
            ],
        ],
    },
    // PACE
    {
        id: "pace-changer-1",
        slide: 2,
        goToSlide: 0,
        label: "PACE",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.arrowLeft} sx={{ fontSize: 10 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        permissions: [
            { list: ["PACE"], operator: "or" },
            { list: ["SAAS_PACE"], operator: "or" },
            { list: ["SYS_ADMIN_VIEW_PACE_PORTAL", "SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON"], operator: "or" },
        ],
    },
    // {
    //     id: "my-dashboard-1",
    //     slide: 2,
    //     label: "My Dashboard",
    //     parentId: undefined,
    //     startIcon: <CustomIcon icon={icons.mainMenuDashboard} sx={{ fontSize: 16 }} />,
    //     endIcon: undefined,
    //     disabled: false,
    //     isSelected: false,
    //     verticalSpace: 1,
    //     base: "/pace",
    //     path: "/my-dashboard",
    //     url: urlPaths.PaceLabsDashboard,
    //     extraPaths: ["/pace/behavioural-comparison"],
    //     updatePathAndItems: updatePaceItems,
    //     permissions: [{ list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" }],
    // },
    {
        id: "behavioural-comparison",
        slide: 2,
        label: "Behavioural Comparison",
        parentId: undefined,
        startIcon: undefined,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        indent: true,
        base: "/behavioural-comparison",
        path: "/pace",
        url: "/pace/behavioural-comparison",
        updatePathAndItems: updatePaceItems,
        permissions: [{ list: ["SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON"], operator: "or" }],
    },
    {
        id: "admin",
        slide: 2,
        label: "PACE Portal",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.equalizer} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/admin",
        path: "/pace",
        url: "/pace/admin",
        updatePathAndItems: updatePaceItems,
        permissions: [{ list: ["SYS_ADMIN_VIEW_PACE_PORTAL"], operator: "or" }],
    },
];
